import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import type { StepExecutionItem } from "../../shared/executions/executionListTypes";
import { useAdjacentFailures } from "../hooks/useStepExecutions";
import { useMakeUrl } from "../lib/urls";
import { zIndexes } from "../lib/zIndexes";
import { executionNavTargets } from "./executionNavTargets";
import { Icon, type IconName } from "./Icon";
import { Link } from "./Link";
import { Tooltip } from "./Tooltip";

const ExecutionNavRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const NavItem = styled("div")`
  position: relative;
  width: 24px;
  height: 24px;

  > .pill {
    position: absolute;
    top: -2px;
    left: 50%;
    transform: translateX(-50%);
    z-index: ${zIndexes.dropdown};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 2px;
    border-radius: 9999px;
    background: transparent;
    box-shadow: none;
  }

  .arrowbtn::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    background: transparent;
    z-index: -1;
    transition: background var(--motion-fast) var(--ease-out);
  }
  .arrowbtn:hover::before,
  .arrowbtn:focus-visible::before {
    background: var(--alpha4);
  }

  /* Hidden (not just transparent) so the jump buttons stay out of the tab
     order until the container opens. */
  > .pill > .extra {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    visibility: hidden;
  }

  /* Open on hover (data-expanded) or keyboard focus so the jumps are reachable
     without a mouse. */
  &[data-expanded] > .pill,
  &:has(:focus-visible) > .pill {
    background: var(--surface-primary);
    box-shadow:
      0 0 0 0.5px var(--border-alpha),
      var(--elevation-low);
  }
  &[data-expanded] > .pill > .extra,
  &:has(:focus-visible) > .pill > .extra {
    max-height: 50px;
    opacity: 1;
    visibility: visible;
  }
`;

const navIconStyles = `
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: var(--text-primary);
  text-decoration: none;
`;

const NavIconLink = styled(Link)`
  ${navIconStyles}
  &:not(.arrowbtn):hover {
    background: var(--alpha4);
  }
`;

const NavIconPlaceholder = styled("span")`
  ${navIconStyles}
  pointer-events: none;
  opacity: 0.4;
`;

const flyoutStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  text-decoration: none;
  &:hover {
    background-color: var(--alpha4);
  }
`;

const FlyoutLink = styled(Link)`
  ${flyoutStyles}
`;

const FlyoutPlaceholder = styled("span")`
  ${flyoutStyles}
  pointer-events: none;
  opacity: 0.4;
`;

export function ExecutionNavTarget({
  id,
  label,
  kind,
  parameter = "execution",
  className,
  children,
}: {
  id: string | undefined;
  label: string;
  kind: "arrow" | "flyout";
  parameter?: "execution" | "run";
  className?: string;
  children: React.ReactNode;
}) {
  const makeUrl = useMakeUrl();
  const target = id ? (
    kind === "arrow" ? (
      <NavIconLink
        className={className}
        to={makeUrl(null, { [parameter]: id })}
        aria-label={label}
      >
        {children}
      </NavIconLink>
    ) : (
      <FlyoutLink
        className={className}
        to={makeUrl(null, { [parameter]: id })}
        aria-label={label}
      >
        {children}
      </FlyoutLink>
    )
  ) : kind === "arrow" ? (
    <NavIconPlaceholder className={className} aria-hidden="true">
      {children}
    </NavIconPlaceholder>
  ) : (
    <FlyoutPlaceholder className={className} aria-hidden="true">
      {children}
    </FlyoutPlaceholder>
  );
  return (
    <Tooltip
      content={label}
      align={kind === "arrow" ? "center" : undefined}
      side={kind === "flyout" ? "left" : undefined}
    >
      {target}
    </Tooltip>
  );
}

const NAV_EXPAND_DELAY_MS = 400;

function NavDirection({
  icon,
  runTooltip,
  successTooltip,
  failureTooltip,
  run,
  success,
  failureId,
}: {
  icon: IconName;
  runTooltip: string;
  successTooltip: string;
  failureTooltip: string;
  run: StepExecutionItem | undefined;
  success: StepExecutionItem | undefined;
  failureId: string | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The flyout must open even when the adjacent run is out of the loaded
  // window, so a failure reachable only through execution_failures stays
  // clickable.
  const hasContent = Boolean(run || success || failureId);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const handleEnter = () => {
    if (!hasContent) return;
    timer.current = setTimeout(() => setExpanded(true), NAV_EXPAND_DELAY_MS);
  };
  const handleLeave = () => {
    if (timer.current) clearTimeout(timer.current);
    setExpanded(false);
  };
  return (
    <NavItem
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-expanded={(expanded || !run) && hasContent ? true : undefined}
    >
      <div className="pill">
        <ExecutionNavTarget
          id={run?.id}
          label={runTooltip}
          kind="arrow"
          className="arrowbtn"
        >
          <Icon name={icon} size={12} />
        </ExecutionNavTarget>
        {hasContent && (
          <div className="extra">
            <ExecutionNavTarget
              id={success?.id}
              label={successTooltip}
              kind="flyout"
            >
              <Icon name="check-filled" size={12} color="var(--green-600)" />
            </ExecutionNavTarget>
            <ExecutionNavTarget
              id={failureId}
              label={failureTooltip}
              kind="flyout"
            >
              <Icon name="check-filled-x" size={12} color="var(--red-600)" />
            </ExecutionNavTarget>
          </div>
        )}
      </div>
    </NavItem>
  );
}

export function ExecutionNav({
  executions,
  selectedExecution,
  workflowId,
  branchId,
  stepDir,
}: {
  executions: StepExecutionItem[];
  selectedExecution: StepExecutionItem;
  workflowId: string;
  branchId: string;
  stepDir: string;
}) {
  const { older, newer, olderSuccess, newerSuccess } = executionNavTargets(
    executions,
    selectedExecution.id
  );
  // Failures cycle the whole retained history, not just the loaded window.
  const failures = useAdjacentFailures(workflowId, branchId, stepDir, {
    id: selectedExecution.id,
    createdAt: selectedExecution.createdAt,
  });
  return (
    <ExecutionNavRow>
      <NavDirection
        icon="arrow-down"
        runTooltip="Previous run"
        successTooltip="Previous successful run"
        failureTooltip="Previous failure"
        run={older}
        success={olderSuccess}
        failureId={failures.older?.id}
      />
      <NavDirection
        icon="arrow-up"
        runTooltip="Next run"
        successTooltip="Next successful run"
        failureTooltip="Next failure"
        run={newer}
        success={newerSuccess}
        failureId={failures.newer?.id}
      />
    </ExecutionNavRow>
  );
}
